import {usePersona} from 'src/permissions/hooks';
import {ConsumptionPaymentMode, UserTypeEnum} from 'src/enums';
import {missingBexioApplies} from 'src/utils/bexio';

/**
 * Whether this record should carry the "no Bexio account" warning.
 *
 * `missingBexioApplies` holds the rule; this holds the half of it that every caller was expected to
 * remember on its own - the warning is a business-side concern and is never shown to a company
 * client - and one caller did forget it: the company-employees grid passed no viewer at all, so a
 * company user looking at their own staff saw red names the rule says they must never see.
 *
 * Callers keep the mapping from their own row shape to the rule's inputs, because only they know it:
 * a user row carries its `userType`, an employee cell is an employee whatever the row says, and a
 * company arrives as an object with a name rather than a type. What none of them decides any more is
 * who is looking.
 *
 * Having the flag as a value rather than as a colour inside a cell is also what lets a card show it:
 * on a phone the warning is a pill in the card's own pills row, since a tooltip has no hover to open
 * it and a red name says nothing to someone who has never seen the black one.
 */
export const useMissingBexio = (params: {
    /** Whether the record already has a Bexio account; the warning is about its absence. */
    hasBexio?: boolean;
    userType?: UserTypeEnum;
    isCompany?: boolean;
    paymentMode?: ConsumptionPaymentMode;
}): boolean => {
    const persona = usePersona();
    const {hasBexio, userType, isCompany, paymentMode} = params;

    return (
        !hasBexio &&
        missingBexioApplies({
            userType,
            isCompany,
            paymentMode,
            viewerIsCompany: persona.isCompany,
        })
    );
};
